// ** React Imports
import { ChangeEvent, MouseEvent, ReactNode, useState } from 'react'

// ** MUI Components
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import InputLabel from '@mui/material/InputLabel'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import CardContent from '@mui/material/CardContent'
import FormControl from '@mui/material/FormControl'
import OutlinedInput from '@mui/material/OutlinedInput'
import { styled } from '@mui/material/styles'
import MuiCard, { CardProps } from '@mui/material/Card'
import InputAdornment from '@mui/material/InputAdornment'
import Stack from '@mui/material/Stack'
import Link from 'next/link'

import { useDispatch } from 'react-redux'
import { userLogin } from 'src/store/user/auth/actions'

// ** Icons Imports
import EyeOutline from 'mdi-material-ui/EyeOutline'
import EyeOffOutline from 'mdi-material-ui/EyeOffOutline'

// ** Layout Import
import { ThemeProvider } from '@mui/material/styles'
import theme from 'src/utils/themeUser'
import Image from 'next/image'

interface State {
  email: string
  password: string
  showPassword: boolean
}

// ** Styled Components
const Card = styled(MuiCard)<CardProps>(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '28rem' },
}))

const LinkStyled = styled(Link)(({ theme }) => ({
  fontSize: '0.875rem',
  textDecoration: 'none',
  color: theme.palette.primary.dark,
}))

const LoginPage = () => {
  // ** State
  const [values, setValues] = useState<State>({
    email: '',
    password: '',
    showPassword: false,
  })

  const dispatch = useDispatch()

  const handleChange = (prop: keyof State) => (event: ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword })
  }

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const onLogin = () => {
    dispatch(userLogin({ email: values.email, password: values.password, via: 'user' }))
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        p={2}
        display='flex'
        alignItems='center'
        justifyContent='center'
        height='100dvh'
        sx={{ backgroundColor: theme.palette.primary.dark }}
      >
        <Card sx={{ zIndex: 1 }}>
          <CardContent sx={{ padding: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Image src={'/chikamichi_logo.svg'} width={138} height={52} alt={'chikamichi_logo'} />
            </Box>
            <Box mb={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant='h6'>ログイン</Typography>
            </Box>
            <form noValidate autoComplete='off' onSubmit={e => e.preventDefault()}>
              <TextField
                autoFocus
                fullWidth
                id='email'
                label='メールアドレス'
                sx={{ marginBottom: 2 }}
                onChange={handleChange('email')}
              />
              <FormControl fullWidth>
                <InputLabel htmlFor='auth-login-password'>パスワード</InputLabel>
                <OutlinedInput
                  label='Password'
                  value={values.password}
                  id='auth-login-password'
                  onChange={handleChange('password')}
                  type={values.showPassword ? 'text' : 'password'}
                  endAdornment={
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        aria-label='toggle password visibility'
                      >
                        {values.showPassword ? <EyeOutline /> : <EyeOffOutline />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </FormControl>
              <Box
                sx={{ mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'space-between' }}
              ></Box>
              <Button fullWidth size='large' variant='contained' onClick={onLogin} type='submit'>
                ログイン
              </Button>
              <Stack flexDirection='row' flexWrap='wrap' alignItems='center' justifyContent='center' mt={2}>
                <Typography variant='body2'>
                  <LinkStyled prefetch href='/user/password/forgot' passHref>
                    パスワードを忘れた方はこちら
                  </LinkStyled>
                </Typography>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </ThemeProvider>
  )
}

LoginPage.getLayout = (page: ReactNode) => <>{page}</>

export default LoginPage
